import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const UserQuestions = () => {
    const { config } = useConfig();
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [newQuestion, setNewQuestion] = useState({ text: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    const pageDesign = config?.pageDesigns?.questions_private || {};
    const primaryColor = config?.primaryColor || '#00595b';
    const secondaryColor = config?.secondaryColor || '#ff5874';
    const tertiaryColor = config?.tertiaryColor || '#008b8b';

    const questionsTitle = pageDesign.title || "Preguntas al expositor";
    const badgeColor = pageDesign.badgeColor || tertiaryColor;
    const boxBorderColor = pageDesign.boxBorderColor || secondaryColor;
    const placeholderText = pageDesign.placeholder || "Escriba su pregunta aquí...";
    const submitBtnText = pageDesign.submit_btn_text || "Enviar Pregunta";
    const submitBtnColor = pageDesign.submit_btn_color || secondaryColor;
    const exitBtnText = pageDesign.exit_btn_text || "Salir";
    const exitBtnColor = pageDesign.exit_btn_color || "#003840";

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 3000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const qRes = await api.get('/questions');
            setQuestions(qRes.data);
        } catch (error) {
            console.error("Error fetching questions", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newQuestion.text) return;

        setIsSubmitting(true);
        setMessage('');
        try {
            await api.post('/questions', { ...newQuestion, speaker_id: null });
            setMessage('¡Pregunta enviada con éxito!');
            setNewQuestion({ text: '' });
            fetchData();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error submitting question", error);
            setMessage('Error al enviar la pregunta.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-md mx-auto animate-fadeIn pb-20 pt-8 px-4 font-sans">
            {/* Form Section matching PDF Page 4 Screen 2 */}
            <div className="relative mb-8">
                {/* Floating Top Badge Header */}
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-20 w-[85%] flex justify-center">
                    <div
                        style={{ backgroundColor: badgeColor }}
                        className="text-white py-2.5 px-6 text-center font-bold text-sm sm:text-base rounded-xl shadow-md capitalize tracking-wider w-full"
                    >
                        {questionsTitle}
                    </div>
                </div>

                {/* Main Card Box */}
                <div
                    style={{ borderColor: boxBorderColor }}
                    className="bg-white border-2 rounded-3xl pt-12 pb-6 px-6 sm:px-8 relative shadow-2xl min-h-[200px]"
                >
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <textarea
                                className="w-full bg-slate-50 border-2 border-slate-200 focus:border-[#ff5874] p-4 rounded-xl focus:outline-none transition-all font-bold text-slate-800 text-sm shadow-inner placeholder:text-slate-300 min-h-[120px] resize-none"
                                placeholder={placeholderText}
                                value={newQuestion.text}
                                onChange={e => setNewQuestion({ ...newQuestion, text: e.target.value })}
                                required
                            />
                        </div>

                        {message && (
                            <div className={`text-center text-xs font-black capitalize tracking-wider p-3 rounded-lg border ${message.includes('éxito') ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                                {message}
                            </div>
                        )}
                    </form>
                </div>

                {/* Submit Button */}
                <div className="w-[85%] mx-auto mt-5 flex justify-center relative z-20">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || !newQuestion.text}
                        style={{ backgroundColor: submitBtnColor }}
                        className="w-full text-white py-4 rounded-xl font-black capitalize tracking-widest text-[13px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                    >
                        {isSubmitting ? 'Enviando...' : submitBtnText}
                    </button>
                </div>
            </div>

            {/* Exit Button */}
            <div className="flex justify-center mt-6 mb-8">
                <button
                    onClick={() => navigate('/')}
                    style={{ backgroundColor: exitBtnColor }}
                    className="w-[85%] text-white py-3.5 font-black text-sm sm:text-base rounded-2xl shadow-lg transition-all hover:scale-[1.02] active:scale-95 text-center"
                >
                    {exitBtnText}
                </button>
            </div>

            {/* Questions History */}
            {questions.length > 0 && (
                <div className="mt-8 pt-6 border-t border-white/20">
                    <h3 className="text-xs font-bold text-white/80 uppercase tracking-widest mb-4 text-center">Mis Preguntas Enviadas</h3>
                    <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                        {questions.map(q => (
                            <div key={q.id} className="bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-sm border border-slate-100">
                                <div className="flex justify-between items-start mb-1">
                                    <span style={{ color: primaryColor }} className="text-[10px] font-black uppercase tracking-wider">Pregunta</span>
                                    <span className="text-[9px] text-slate-400 font-medium">{new Date(q.created_at).toLocaleDateString()}</span>
                                </div>
                                <p className="text-slate-800 font-semibold text-xs sm:text-sm leading-relaxed">{q.text}</p>

                                {q.reply ? (
                                    <div className="bg-slate-100 p-3 rounded-lg mt-2 border border-slate-200">
                                        <span style={{ color: secondaryColor }} className="block text-[9px] font-black uppercase mb-1">Respuesta</span>
                                        <p className="text-slate-800 text-xs font-medium leading-relaxed">{q.reply}</p>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></div>
                                        Pendiente de respuesta
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserQuestions;
